import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../api/axiosConfig';
import swimService from '../api/services/swimService';
import { ClassLevel } from '../types/swim';
import SwimProgramFormDialog, {
  SwimProgramFormData,
  emptyForm,
} from '../components/swim/SwimProgramFormDialog';
import { ResponsiveTable, ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import { useSnackbar } from '../components/common/SnackbarProvider';

interface SwimProgramRow {
  id: string;
  name: string;
  description: string | null;
  program_type: string;
  status: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
  price_member: number;
  price_non_member: number | null;
  max_participants: number;
  class_level_id: string | null;
  required_consents: string[] | null;
  recurrence_pattern: { days: number[]; time: string; duration: number } | null;
  target_audience?: 'kids' | 'adult' | 'all' | string;
  members_only?: boolean;
  enrollment_count?: number;
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function to12Hour(hhmm: string): string {
  const [hStr, m] = (hhmm || '').split(':');
  const h = Number(hStr);
  if (!Number.isFinite(h)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m ?? '00'} ${period}`;
}

function formatSchedule(p: SwimProgramRow['recurrence_pattern']): string {
  if (!p) return '—';
  const days = (p.days || []).map((d) => DAY_NAMES[d] ?? '?').join(',');
  return `${days} ${to12Hour(p.time)} (${p.duration}m)`;
}

const statusColor: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  draft: 'warning',
  published: 'success',
  cancelled: 'error',
  completed: 'default',
};

const SwimProgramsAdminPage: React.FC = () => {
  const snackbar = useSnackbar();
  const [programs, setPrograms] = useState<SwimProgramRow[]>([]);
  const [levels, setLevels] = useState<ClassLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [audienceFilter, setAudienceFilter] = useState<'all' | 'kids' | 'adult'>('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SwimProgramFormData | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [progRes, lvls] = await Promise.all([
        axiosInstance.get<{ data: SwimProgramRow[] }>('/programs/all', {
          params: { include_inactive: 'true' },
        }),
        swimService.listClassLevels({ includeInactive: true }),
      ]);
      const allProgs = progRes.data.data ?? [];
      setPrograms(allProgs.filter((p) => p.program_type === 'swim'));
      setLevels(lvls);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  const levelById = useMemo(() => new Map(levels.map((l) => [l.id, l])), [levels]);

  // Audience filter: 'all' shows everything; 'kids' shows kids+all-audience;
  // 'adult' shows adult+all-audience. Counts in the toggle buttons reflect
  // the same union semantics.
  const audienceOf = (p: SwimProgramRow) => (p.target_audience || 'kids').toLowerCase();
  const visiblePrograms = useMemo(() => {
    if (audienceFilter === 'all') return programs;
    return programs.filter((p) => {
      const a = audienceOf(p);
      return a === audienceFilter || a === 'all';
    });
  }, [programs, audienceFilter]);

  const kidsCount = useMemo(
    () => programs.filter((p) => ['kids', 'all'].includes(audienceOf(p))).length,
    [programs],
  );
  const adultCount = useMemo(
    () => programs.filter((p) => ['adult', 'all'].includes(audienceOf(p))).length,
    [programs],
  );

  function openCreate() {
    setEditing(emptyForm);
    setDialogOpen(true);
  }

  function openEdit(p: SwimProgramRow) {
    setEditing({
      id: p.id,
      name: p.name,
      description: p.description ?? '',
      class_level_id: p.class_level_id ?? '',
      start_date: p.start_date,
      end_date: p.end_date,
      recurrence_days: p.recurrence_pattern?.days ?? [],
      recurrence_time: p.recurrence_pattern?.time ?? '16:00',
      recurrence_duration: p.recurrence_pattern?.duration ?? 60,
      price_member: String(p.price_member),
      price_non_member: p.price_non_member != null ? String(p.price_non_member) : '',
      max_participants: String(p.max_participants),
      required_consents: p.required_consents ?? [],
      target_audience: (p.target_audience as 'kids' | 'adult' | 'all') ?? 'kids',
      members_only: !!p.members_only,
    });
    setDialogOpen(true);
  }

  async function publish(p: SwimProgramRow) {
    setBusy(true);
    try {
      await axiosInstance.post(`/programs/${p.id}/publish`);
      await load();
    } catch (e) {
      snackbar.error(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setBusy(false);
    }
  }

  async function cancel(p: SwimProgramRow) {
    if (!window.confirm(
      `Cancel "${p.name}"?\n\nThe program will be marked cancelled. Existing enrollees will still see it as cancelled; ` +
      `new parents won't see it on the catalog. Use Delete to remove it permanently.`,
    )) return;
    setBusy(true);
    try {
      await axiosInstance.delete(`/programs/${p.id}`);
      await load();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Cancel failed');
    } finally {
      setBusy(false);
    }
  }

  async function reactivate(p: SwimProgramRow) {
    setBusy(true);
    try {
      await axiosInstance.post(`/programs/${p.id}/reactivate`);
      await load();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Reactivate failed');
    } finally {
      setBusy(false);
    }
  }

  async function destroy(p: SwimProgramRow) {
    if (!window.confirm(
      `Permanently DELETE "${p.name}"?\n\nThis cannot be undone. Refuses if there are still ` +
      `active enrollments — cancel the program first or cancel individual enrollments.`,
    )) return;
    setBusy(true);
    try {
      await axiosInstance.delete(`/programs/${p.id}/destroy`);
      await load();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" component="h1">Swim Programs</Typography>
          <Typography variant="body2" color="text.secondary">
            Scheduled swim classes parents can enroll in. Each program is assigned to a Swim Skill Level.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={() => void load()} disabled={loading || busy}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Program
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined">
        <Toolbar
          sx={{
            minHeight: '56px !important',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1.5,
            py: { xs: 1.5, sm: 0 },
          }}
        >
          <Typography variant="subtitle1" sx={{ flex: 1 }}>
            {visiblePrograms.length} swim program{visiblePrograms.length === 1 ? '' : 's'}
            {audienceFilter !== 'all' && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                ({audienceFilter === 'kids' ? 'Kids' : 'Adults'} only)
              </Typography>
            )}
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={audienceFilter}
            exclusive
            onChange={(_e, v) => v && setAudienceFilter(v)}
          >
            <ToggleButton value="all">All ({programs.length})</ToggleButton>
            <ToggleButton value="kids">Kids ({kidsCount})</ToggleButton>
            <ToggleButton value="adult">Adults ({adultCount})</ToggleButton>
          </ToggleButtonGroup>
        </Toolbar>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (() => {
          // Action button group — shared between desktop row + mobile card
          const renderActions = (p: SwimProgramRow) => (
            <>
              <IconButton onClick={() => openEdit(p)} disabled={busy} title="Edit">
                <EditIcon fontSize="small" />
              </IconButton>
              {p.status === 'draft' && p.is_active && (
                <IconButton onClick={() => void publish(p)} disabled={busy} title="Publish">
                  <PublishIcon fontSize="small" />
                </IconButton>
              )}
              {(p.status === 'draft' || p.status === 'published') && p.is_active && (
                <IconButton onClick={() => void cancel(p)} disabled={busy} title="Cancel program">
                  <BlockIcon fontSize="small" />
                </IconButton>
              )}
              {(p.status === 'cancelled' || !p.is_active) && (
                <IconButton onClick={() => void reactivate(p)} disabled={busy} title="Reactivate (returns to draft)" color="primary">
                  <RestoreIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton onClick={() => void destroy(p)} disabled={busy} title="Permanently delete" color="error">
                <DeleteForeverIcon fontSize="small" />
              </IconButton>
            </>
          );

          const renderStatusChip = (p: SwimProgramRow) => (
            <Chip
              size="small"
              label={p.is_active ? p.status : 'inactive'}
              color={p.is_active ? (statusColor[p.status] ?? 'default') : 'default'}
              variant={p.is_active ? 'filled' : 'outlined'}
            />
          );

          const columns: ResponsiveTableColumn<SwimProgramRow>[] = [
            {
              key: 'name',
              label: 'Name',
              renderCell: (p) => (
                <>
                  <Typography variant="body2"><strong>{p.name}</strong></Typography>
                  {p.description && (
                    <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                  )}
                </>
              ),
            },
            {
              key: 'level',
              label: 'Level',
              renderCell: (p) => {
                const lvl = p.class_level_id ? levelById.get(p.class_level_id) : null;
                return lvl
                  ? <Chip size="small" label={lvl.name} color="primary" variant="outlined" />
                  : <Typography variant="caption" color="text.secondary">—</Typography>;
              },
            },
            {
              key: 'audience',
              label: 'Audience',
              renderCell: (p) => {
                const a = audienceOf(p);
                const label = a === 'adult' ? 'Adults' : a === 'all' ? 'All' : 'Kids';
                const color: 'primary' | 'secondary' | 'default' =
                  a === 'adult' ? 'secondary' : a === 'all' ? 'default' : 'primary';
                return <Chip size="small" label={label} color={color} variant="outlined" />;
              },
            },
            {
              key: 'schedule',
              label: 'Schedule',
              renderCell: (p) => <Typography variant="caption">{formatSchedule(p.recurrence_pattern)}</Typography>,
            },
            {
              key: 'dates',
              label: 'Dates',
              renderCell: (p) => <Typography variant="caption">{p.start_date} → {p.end_date}</Typography>,
            },
            {
              key: 'price',
              label: 'Price',
              align: 'right',
              renderCell: (p) => (
                <>
                  <Typography variant="body2">${Number(p.price_member).toFixed(0)}</Typography>
                  {p.price_non_member != null && (
                    <Typography variant="caption" color="text.secondary">
                      ${Number(p.price_non_member).toFixed(0)} nm
                    </Typography>
                  )}
                </>
              ),
            },
            {
              key: 'capacity',
              label: 'Capacity',
              align: 'right',
              renderCell: (p) => (
                <Typography variant="body2">{p.enrollment_count ?? 0} / {p.max_participants}</Typography>
              ),
            },
            { key: 'status', label: 'Status', renderCell: renderStatusChip },
            { key: 'actions', label: 'Actions', align: 'right', renderCell: renderActions },
          ];

          return (
            <ResponsiveTable<SwimProgramRow>
              rows={visiblePrograms}
              columns={columns}
              rowKey={(p) => p.id}
              emptyState={
                <Typography color="text.secondary">
                  No swim programs yet. Click "New Program" to create one.
                </Typography>
              }
              renderMobileCard={(p) => {
                const lvl = p.class_level_id ? levelById.get(p.class_level_id) : null;
                return (
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1"><strong>{p.name}</strong></Typography>
                        {p.description && (
                          <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                        )}
                      </Box>
                      {renderStatusChip(p)}
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {lvl && <Chip size="small" label={lvl.name} color="primary" variant="outlined" />}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${p.enrollment_count ?? 0} / ${p.max_participants} enrolled`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`$${Number(p.price_member).toFixed(0)} member${
                          p.price_non_member != null ? ` · $${Number(p.price_non_member).toFixed(0)} nm` : ''
                        }`}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {formatSchedule(p.recurrence_pattern)} • {p.start_date} → {p.end_date}
                    </Typography>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {renderActions(p)}
                    </Stack>
                  </Stack>
                );
              }}
            />
          );
        })()}
      </Paper>

      <SwimProgramFormDialog
        open={dialogOpen}
        initial={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          setDialogOpen(false);
          void load();
        }}
      />
    </Container>
  );
};

export default SwimProgramsAdminPage;
